import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  CMSData,
  ApartmentData,
  GalleryImage,
  HeroData,
  AboutData,
  LocationData,
  ContactData,
  FooterData,
  SEOData,
  PaymentPlan,
  BrandingData,
} from '../cms/types';
import { loadCMSData, saveCMSData, defaultCMSData } from '../cms/cmsData';
import { supabase } from '../lib/supabase';

interface CMSContextValue {
  cmsData: CMSData;
  isLoading: boolean;
  isSaving: boolean;
  saveError: string | null;
  lastSaveTime: Date | null;
  isDirty: boolean;
  manualSave: () => Promise<void>;
  updateCMSData: (updater: Partial<CMSData> | ((prev: CMSData) => CMSData)) => void;
  updateHero: (hero: Partial<HeroData>) => void;
  updateAbout: (about: Partial<AboutData>) => void;
  updateApartments: (apartments: ApartmentData[]) => void;
  addApartment: (apartment: ApartmentData) => void;
  removeApartment: (id: string) => void;
  updateGallery: (gallery: GalleryImage[]) => void;
  addGalleryImage: (image: GalleryImage) => void;
  removeGalleryImage: (id: string) => void;
  updateLocation: (location: Partial<LocationData>) => void;
  updateContact: (contact: Partial<ContactData>) => void;
  updateFooter: (footer: Partial<FooterData>) => void;
  updateSEO: (page: string, seo: Partial<SEOData[string]>) => void;
  updatePaymentPlans: (paymentPlans: PaymentPlan[]) => void;
  updateBranding: (branding: Partial<BrandingData>) => void;
}

const CMSContext = createContext<CMSContextValue | null>(null);

// Autosave debounce delay (ms)
const AUTOSAVE_DELAY = 2000;
// Max save retries
const MAX_RETRIES = 3;
// Admin-only polling interval (ms) — only used on /admin
const ADMIN_POLL_INTERVAL = 60000;

// Simple in-memory cache so public pages don't re-fetch on every navigation
let memoryCache: CMSData | null = null;
let memoryCacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function CMSProvider({ children }: { children: ReactNode }) {
  const [cmsData, setCmsData] = useState<CMSData>(memoryCache ?? defaultCMSData);
  const [isLoading, setIsLoading] = useState(!memoryCache);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDataRef = useRef<CMSData | null>(null);
  const isSavingRef = useRef(false); // Track save state without re-renders
  const saveBlockUntilRef = useRef<number>(0); // Block remote updates for N ms after save
  const isAdminPage = window.location.pathname.startsWith('/admin');

  // Apply branding (favicon + OG image) to the document head whenever it changes
  useEffect(() => {
    const { faviconUrl, ogImageUrl } = cmsData.branding || {};
    if (faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'icon');
        document.head.appendChild(link);
      }
      link.setAttribute('href', faviconUrl);
      link.setAttribute('type', faviconUrl.endsWith('.png') ? 'image/png' : 'image/jpeg');
    }
    if (ogImageUrl) {
      const setMeta = (attr: string, name: string, content: string) => {
        let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };
      setMeta('property', 'og:image', ogImageUrl);
      setMeta('name', 'twitter:image', ogImageUrl);
    }
  }, [cmsData.branding]);

  // Initial data load — use cache for public pages, always fresh for admin
  useEffect(() => {
    const now = Date.now();
    const cacheValid = memoryCache && (now - memoryCacheTime) < CACHE_TTL;

    // Public pages: use cache if available, skip fetch
    if (!isAdminPage && cacheValid && memoryCache) {
      setCmsData(memoryCache);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await loadCMSData();
        memoryCache = data;
        memoryCacheTime = Date.now();
        setCmsData(data);
      } catch (error) {
        console.error('Failed to load CMS data:', error);
        // Fall back to defaults silently
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin-only: Realtime subscription + light polling
  useEffect(() => {
    if (!isAdminPage) return;

    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const applyRemoteData = (rawData: Partial<CMSData>) => {
      // Don't overwrite local changes while saving or shortly after a save
      if (isSavingRef.current || Date.now() < saveBlockUntilRef.current) return;

      function deepMergeLocal<T extends object>(defaults: T, saved: Partial<T>): T {
        const result = { ...defaults } as T;
        for (const key in saved) {
          const savedVal = saved[key];
          const defaultVal = defaults[key];
          if (
            savedVal !== null &&
            savedVal !== undefined &&
            typeof savedVal === 'object' &&
            !Array.isArray(savedVal) &&
            typeof defaultVal === 'object' &&
            !Array.isArray(defaultVal)
          ) {
            result[key] = deepMergeLocal(defaultVal as object, savedVal as object) as T[typeof key];
          } else if (savedVal !== undefined) {
            result[key] = savedVal as T[typeof key];
          }
        }
        return result;
      }
      const merged = deepMergeLocal(defaultCMSData, rawData);
      memoryCache = merged;
      memoryCacheTime = Date.now();
      setCmsData(merged);
    };

    const fetchLatest = async () => {
      try {
        const { data, error } = await supabase
          .from('cms_content')
          .select('data')
          .eq('id', 'capella_cms_v1')
          .maybeSingle();
        if (!error && data?.data) {
          applyRemoteData(data.data as CMSData);
        }
      } catch (err) {
        console.error('CMS admin fetch failed:', err);
      }
    };

    const subscribe = () => {
      const channel = supabase
        .channel('cms_admin_channel')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'cms_content' },
          async () => { await fetchLatest(); }
        )
        .subscribe((status, err) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn('Realtime channel error, retrying in 10s...', err);
            supabase.removeChannel(channel);
            retryTimeout = setTimeout(subscribe, 10000);
          }
        });
      return channel;
    };

    const channel = subscribe();
    // Light polling only on admin — every 60s instead of 30s
    pollInterval = setInterval(fetchLatest, ADMIN_POLL_INTERVAL);

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (pollInterval) clearInterval(pollInterval);
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save with retry logic
  const executeSave = useCallback(async (dataToSave: CMSData, attempt = 1): Promise<void> => {
    isSavingRef.current = true;
    setIsSaving(true);
    setSaveError(null);
    try {
      await saveCMSData(dataToSave);
      memoryCache = dataToSave;
      memoryCacheTime = Date.now();
      setLastSaveTime(new Date());
      setIsDirty(false);
      // Block remote overwrites for 5 seconds after a successful save
      saveBlockUntilRef.current = Date.now() + 5000;
    } catch (error) {
      console.error(`Save attempt ${attempt} failed:`, error);
      if (attempt < MAX_RETRIES) {
        await new Promise((res) => setTimeout(res, 1000 * attempt));
        return executeSave(dataToSave, attempt + 1);
      }
      setSaveError('Failed to save changes. Please check your connection and try again.');
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, []);

  // Schedules an autosave after AUTOSAVE_DELAY ms of inactivity
  const scheduleAutosave = useCallback((data: CMSData) => {
    pendingDataRef.current = data;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      if (pendingDataRef.current) {
        executeSave(pendingDataRef.current);
        pendingDataRef.current = null;
      }
    }, AUTOSAVE_DELAY);
  }, [executeSave]);

  const updateCMSData = useCallback(
    (updater: Partial<CMSData> | ((prev: CMSData) => CMSData)) => {
      setCmsData((prev) => {
        const newData =
          typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
        setIsDirty(true);
        scheduleAutosave(newData);
        return newData;
      });
    },
    [scheduleAutosave]
  );

  const manualSave = useCallback(async () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    pendingDataRef.current = null;
    await executeSave(cmsData);
  }, [cmsData, executeSave]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, []);

  const updateHero = useCallback(
    (hero: Partial<HeroData>) =>
      updateCMSData((prev) => ({ ...prev, hero: { ...prev.hero, ...hero } })),
    [updateCMSData]
  );

  const updateAbout = useCallback(
    (about: Partial<AboutData>) =>
      updateCMSData((prev) => ({ ...prev, about: { ...prev.about, ...about } })),
    [updateCMSData]
  );

  const updateApartments = useCallback(
    (apartments: ApartmentData[]) => updateCMSData((prev) => ({ ...prev, apartments })),
    [updateCMSData]
  );

  const addApartment = useCallback(
    (apartment: ApartmentData) =>
      updateCMSData((prev) => ({ ...prev, apartments: [...prev.apartments, apartment] })),
    [updateCMSData]
  );

  const removeApartment = useCallback(
    (id: string) =>
      updateCMSData((prev) => ({
        ...prev,
        apartments: prev.apartments.filter((apt) => apt.id !== id),
      })),
    [updateCMSData]
  );

  const updateGallery = useCallback(
    (gallery: GalleryImage[]) => updateCMSData((prev) => ({ ...prev, gallery })),
    [updateCMSData]
  );

  const addGalleryImage = useCallback(
    (image: GalleryImage) =>
      updateCMSData((prev) => ({ ...prev, gallery: [...prev.gallery, image] })),
    [updateCMSData]
  );

  const removeGalleryImage = useCallback(
    (id: string) =>
      updateCMSData((prev) => ({
        ...prev,
        gallery: prev.gallery.filter((img) => img.id !== id),
      })),
    [updateCMSData]
  );

  const updateLocation = useCallback(
    (location: Partial<LocationData>) =>
      updateCMSData((prev) => ({ ...prev, location: { ...prev.location, ...location } })),
    [updateCMSData]
  );

  const updateContact = useCallback(
    (contact: Partial<ContactData>) =>
      updateCMSData((prev) => ({ ...prev, contact: { ...prev.contact, ...contact } })),
    [updateCMSData]
  );

  const updateFooter = useCallback(
    (footer: Partial<FooterData>) =>
      updateCMSData((prev) => ({ ...prev, footer: { ...prev.footer, ...footer } })),
    [updateCMSData]
  );

  const updateSEO = useCallback(
    (page: string, seo: Partial<SEOData[string]>) =>
      updateCMSData((prev) => ({
        ...prev,
        seo: { ...prev.seo, [page]: { ...prev.seo[page], ...seo } },
      })),
    [updateCMSData]
  );

  const updatePaymentPlans = useCallback(
    (paymentPlans: PaymentPlan[]) =>
      updateCMSData((prev) => ({ ...prev, paymentPlans })),
    [updateCMSData]
  );

  const updateBranding = useCallback(
    (branding: Partial<BrandingData>) =>
      updateCMSData((prev) => ({ ...prev, branding: { ...prev.branding, ...branding } })),
    [updateCMSData]
  );

  const value: CMSContextValue = {
    cmsData,
    isLoading,
    isSaving,
    saveError,
    lastSaveTime,
    isDirty,
    manualSave,
    updateCMSData,
    updateHero,
    updateAbout,
    updateApartments,
    addApartment,
    removeApartment,
    updateGallery,
    addGalleryImage,
    removeGalleryImage,
    updateLocation,
    updateContact,
    updateFooter,
    updateSEO,
    updatePaymentPlans,
    updateBranding,
  };

  return <CMSContext.Provider value={value}>{children}</CMSContext.Provider>;
}

export function useCMSContext(): CMSContextValue {
  const ctx = useContext(CMSContext);
  if (!ctx) throw new Error('useCMSContext must be used inside CMSProvider');
  return ctx;
}
